import { initSentry, Sentry } from "./lib/sentry";
initSentry();

import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { AuthProvider, useAuth } from "./lib/auth";
import { router } from "./router";
import { LoginPage } from "./features/auth/LoginPage";
import { ForgotPasswordPage } from "./features/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./features/auth/ResetPasswordPage";
import { VerifyEmailPage } from "./features/auth/VerifyEmailPage";
import { AcceptInvitePage } from "./features/auth/AcceptInvitePage";
import { SetupNetworkPage } from "./features/auth/SetupNetworkPage";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

const PUBLIC_ROUTES: Record<string, React.FC> = {
  "/forgot-password": ForgotPasswordPage,
  "/reset-password": ResetPasswordPage,
  "/verify-email": VerifyEmailPage,
  "/accept-invite": AcceptInvitePage,
  "/setup-network": SetupNetworkPage,
};

function getPortalRedirect(userType: string | undefined, path: string): string | null {
  if (!userType) return null;
  if (userType === "super_admin" && !path.startsWith("/super-admin")) return "/super-admin";
  if (userType === "affiliate" && !path.startsWith("/portal/affiliate")) return "/portal/affiliate";
  if (userType === "advertiser" && !path.startsWith("/portal/advertiser")) return "/portal/advertiser";
  return null;
}

function InnerApp() {
  const { token, user, isLoading } = useAuth();
  const redirected = useRef(false);

  useEffect(() => {
    if (!user || redirected.current) return;
    const target = getPortalRedirect(user.userType, window.location.pathname);
    if (target) {
      redirected.current = true;
      window.location.replace(target);
    }
  }, [user]);

  const PublicPage = PUBLIC_ROUTES[window.location.pathname];
  if (PublicPage) {
    return <PublicPage />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (!token) {
    return <LoginPage />;
  }

  // Wait for user data before rendering, to avoid flashing the wrong layout
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  // Block affiliate/advertiser users from accessing admin routes
  const redirect = getPortalRedirect(user.userType, window.location.pathname);
  if (redirect) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return <RouterProvider router={router} />;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-xl font-bold text-gray-900">Something went wrong</h1>
          <p className="text-sm text-gray-500 mt-2">The error has been reported. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800">
            Refresh
          </button>
        </div>
      </div>
    }>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
